/**
 * Payload presets' fields (docs/cases.md §Case page · ITSM, §Settings · ITSM):
 * key / value rows turned into the one JSON object a preset saves (Epic 19;
 * the same object as a sync's `extra_payload`, Epic 17). Checked as the service
 * checks it (case_mgmt_service app/itsm/extra_payload.py validate_extra):
 * containers nest at most 8 deep (the top object is 1), at most 200 keys
 * over every object, at most 16,384 bytes as compact UTF-8 JSON. Pure.
 */

export type PayloadValueType = 'text' | 'json'

export interface PayloadRow {
  key: string
  type: PayloadValueType
  value: string
}

export const PAYLOAD_CAPS = { bytes: 16_384, depth: 8, keys: 200 } as const

export interface PayloadCheck {
  /** The object to send, from the rows without an error. */
  object: Record<string, unknown>
  /** One per row: why it cannot be sent, or null. */
  errors: (string | null)[]
  bytes: number
  depth: number
  keys: number
  /** At least one row, no row error, within every cap. */
  ok: boolean
}

/** Container levels (objects and arrays) — the top object counts as 1; scalars add none. */
function containerDepth(v: unknown): number {
  if (Array.isArray(v)) return 1 + Math.max(0, ...v.map(containerDepth))
  if (v !== null && typeof v === 'object') return 1 + Math.max(0, ...Object.values(v).map(containerDepth))
  return 0
}

/** Keys of every object, nested ones included (arrays add none of their own). */
function keyCount(v: unknown): number {
  if (Array.isArray(v)) return v.reduce((n: number, x) => n + keyCount(x), 0)
  if (v !== null && typeof v === 'object') return Object.keys(v).length + Object.values(v).reduce((n: number, x) => n + keyCount(x), 0)
  return 0
}

const byteSize = (obj: unknown) => new TextEncoder().encode(JSON.stringify(obj)).length

/** The service's check of one payload object: the code it refuses it with, or null (the demo answers with it too). */
export function payloadRefusalCode(obj: unknown): string | null {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return 'not_object'
  if (containerDepth(obj) > PAYLOAD_CAPS.depth) return 'too_deep'
  if (keyCount(obj) > PAYLOAD_CAPS.keys) return 'too_many_keys'
  if (byteSize(obj) > PAYLOAD_CAPS.bytes) return 'too_large'
  return null
}

export function payloadFromRows(rows: readonly PayloadRow[]): PayloadCheck {
  const seen = new Map<string, number>()
  for (const r of rows) {
    const k = r.key.trim()
    if (k) seen.set(k, (seen.get(k) ?? 0) + 1)
  }
  const object: Record<string, unknown> = {}
  const errors = rows.map((r) => {
    const k = r.key.trim()
    if (!k) return 'Give the field a key.'
    if ((seen.get(k) ?? 0) > 1) return `“${k}” is used twice.`
    if (r.type === 'text') {
      object[k] = r.value
      return null
    }
    try {
      object[k] = JSON.parse(r.value) as unknown
      return null
    } catch {
      return 'Not valid JSON. To send it as a string, switch to Text.'
    }
  })
  const bytes = byteSize(object)
  const depth = containerDepth(object)
  const keys = keyCount(object)
  const within = bytes <= PAYLOAD_CAPS.bytes && depth <= PAYLOAD_CAPS.depth && keys <= PAYLOAD_CAPS.keys
  return { object, errors, bytes, depth, keys, ok: rows.length > 0 && errors.every((e) => e === null) && within }
}

/** A saved payload back into rows: each top-level key a row — a string as Text, anything else as pretty JSON. */
export function rowsFromPayload(payload: Record<string, unknown>): PayloadRow[] {
  return Object.entries(payload).map(([key, v]) => (typeof v === 'string' ? { key, type: 'text', value: v } : { key, type: 'json', value: JSON.stringify(v, null, 2) }))
}

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v)

/**
 * Presets combined in the listed order, as the service does before its final
 * merge (ADR 0023 `combine_presets`): objects merge key by key, the later
 * preset winning; an array or a value replaces; `null` is kept, so it still
 * removes the field from the ticket. Pure.
 */
export function combinePresets(payloads: readonly Record<string, unknown>[]): Record<string, unknown> {
  const merge = (a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = { ...a }
    for (const [k, v] of Object.entries(b)) out[k] = isObject(v) && isObject(out[k]) ? merge(out[k], v) : v
    return out
  }
  return payloads.reduce<Record<string, unknown>>((acc, p) => merge(acc, p), {})
}

/** The presets API's error codes (Epic 19), in words. */
const PRESET_REFUSAL: Record<string, string> = {
  preset_name_taken: 'That name is already used for this connection.',
  preset_limit_reached: 'This connection already has 20 presets — delete one first.',
  preset_invalid: 'The name or the payload breaks a limit, or the payload is not a JSON object.',
  preset_not_found: 'A preset was changed or deleted elsewhere. The list is reloaded — check the picks and try again.',
  webhook_not_found: 'The connection is gone.',
  not_implemented: 'Payload presets are not available on this service.',
  presets_not_implemented: 'Payload presets are not available on this service; nothing was sent.',
  // Sync: the picked presets combined break an Epic 17 cap; nothing was sent.
  extra_payload_too_large: 'The picked presets together are over 16 KiB — pick fewer. Nothing was sent.',
  extra_payload_too_deep: 'The picked presets together nest deeper than 8 levels. Nothing was sent.',
  extra_payload_too_many_keys: 'The picked presets together have more than 200 keys — pick fewer. Nothing was sent.',
  extra_payload_bad_key: 'A picked preset has a key the service does not allow. Nothing was sent.',
  extra_payload_not_object: 'A picked preset is not a JSON object. Nothing was sent.',
}

/** A presets error in words, from its code (`cmErrorCode`), else the message. */
export const presetRefusal = (code: string | null, message: string): string => (code && PRESET_REFUSAL[code]) || message
